import { FormControlLabel, Switch, Paper } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import * as React from 'react';
import { Subject, Subscription } from 'rxjs';
import { useHookSubscribe } from '../../../react-rxjs';
import { useMapDimension } from '../../hooks/useMapDimension';
import { useMapStore } from '../../hooks/useMapStore';
import { MapDataSourceId } from '../../models/MapDataSource';
import { MapDataRange } from '../../models/MapDataRange';
import { ChartMap } from '../ChartMap';
import { LeafletComponent, LeafletTooltip, LeafletMarker } from '../LeafletMap';
import { useLeafletTooltip } from '../LeafletMap/Leaflet.hooks';
import geoOptions from './geo/options';
import { eu28Pipe, setDataPipe, setIsEU28Pipe, setISO2CDPipe, colorPipe } from './geo/pipeline';
import { ControlEU28 } from './components/ControlEU28';
import { Tooltip } from './components/Tooltip';
import { Loader } from './components/Loader';
import { Marker } from './components/Marker';
import { noop } from 'lodash';

type BASFChartMapType = React.FC<{ options?: L.MapOptions, onMount?: (map: L.Map, setterEU28: (isEU28: boolean) => void) => void }>;

export const BASFChartMap: BASFChartMapType = React.memo(React.forwardRef((props, ref) => {
  const { options, onMount = noop } = props;
  const { handlerMount, handlerEU28, geoPipeline, loading, ranges, useAutoFocus, fitWorld } = useBASFMap(onMount);

  return <div style={{ position: 'relative', display: 'flex', flex: 1 }} ref={ref as any}>
    {loading && <Loader><Skeleton width="100%" height="100%" variant="rect" /></Loader>}
    <ChartMap options={options} geoOptions={geoOptions} geoPipeline={geoPipeline} ranges={ranges} hoverColor="#c57524" useAutoFocus={useAutoFocus} fitWorld={fitWorld} onMount={handlerMount}>
      <LeafletComponent id="showEU28" options={{ position: 'topleft' }}>
        <ControlEU28 onChange={handlerEU28} />
      </LeafletComponent>
      <LeafletTooltip layerId="GEO-LAYER">
        <Tooltip />
      </LeafletTooltip>
      <Marker />
    </ChartMap>
  </div>
}));

export const BASFExportChartMap: BASFChartMapType = React.memo(React.forwardRef((props, ref) => {
  const { options, onMount = noop } = props;
  const mapOptions = React.useMemo(() => ({ zoomControl: false, ...options }), [options]);
  const { setIsEU28, geoPipeline, ranges } = useBASFMap(onMount);
  const handlerMount = React.useCallback((map: L.Map) => onMount(map, setIsEU28), [setIsEU28, onMount]);
  return <div style={{ position: 'relative', display: 'flex', flex: 1 }} ref={ref as any}>
    <ChartMap options={mapOptions} geoOptions={geoOptions} geoPipeline={geoPipeline} ranges={ranges} useAutoFocus={false} fitWorld={false} onMount={handlerMount}>
      <Marker />
    </ChartMap>
  </div>
}));

function useBASFMap(onMount: (map: L.Map, setterEu28: (isEU28: boolean) => void) => void) {
  const [isEU28, setIsEU28] = React.useState(false);
  const [useAutoFocus, setUseAutoFocus] = React.useState(false);
  const store = useMapStore();
  const dataSourceId = useHookSubscribe(MapDataSourceId.Default, store.dataSourceId$);
  const dimensions = useHookSubscribe([], store.dimensions$);

  const [stateMap, setStateMap] = React.useState<L.Map | undefined>();
  const handlerMount = React.useCallback((map: L.Map) => {
    onMount(map, setIsEU28);
    setStateMap(map);
  }, [setIsEU28, onMount]);

  const handlerEU28 = React.useCallback((isEU28: boolean) => {
    setUseAutoFocus(false);
    setIsEU28(isEU28);
    if (stateMap) {
      stateMap['isEU28'] = isEU28;
    }
  }, [stateMap]);

  React.useEffect(() => {
    const subscriptions: Subscription[] = [];
    if (dataSourceId === MapDataSourceId.HarmonizedDataset) {
      const businessUnitDim = dimensions.find(e => e.hierarchy.includes('countries') && e.dataSource?.id === dataSourceId);
      if (businessUnitDim) {
        const value$ = store.dimensionsValues.get(businessUnitDim.id);
        const subscription = value$?.subscribe(() => {
          setUseAutoFocus(true);
        });
        if (subscription) {
          subscriptions.push(subscription);
        }

        dimensions.forEach(dim => {
          if (businessUnitDim !== dim) {
            const val$ = store.dimensionsValues.get(dim.id);
            const sbscr = val$?.subscribe(() => {
              setUseAutoFocus(false);
            });
            if (sbscr) {
              subscriptions.push(sbscr);
            }
          }
        });
      }
    } else {
      setUseAutoFocus(false);
    }

    return () => subscriptions.forEach(e => e?.unsubscribe());
  }, [dataSourceId, dimensions]);

  const data = useHookSubscribe(new Map(), store.data$);
  const loadingData = useHookSubscribe(true, store.loadingData$);
  const loadingDataSources = useHookSubscribe(true, store.loadingDataSources$);
  const loadingDimensions = useHookSubscribe(true, store.loadingDimensions$);
  const loading = loadingData || loadingDataSources || loadingDimensions;
  const fitWorld = dataSourceId !== MapDataSourceId.HarmonizedDataset;
  const ranges = React.useMemo(() => MapDataRange.fromData(data), [data]);

  const geoPipeline = React.useMemo(() => {
    const baseline = [setISO2CDPipe, setIsEU28Pipe];
    return (isEU28 ? [eu28Pipe, ...baseline] : baseline).concat(
      [setDataPipe(data), colorPipe(ranges)]
    );
  }, [isEU28, data, ranges]);


  return { isEU28, setIsEU28, geoPipeline, loading, ranges, useAutoFocus, fitWorld, handlerMount, handlerEU28 }
}